/**
 * Reading a list's filters back out of its address (TRKC-90). The posting board and the
 * load book keep what they are narrowed to in the URL, so going back to them — from a
 * record, say — or opening a shared link shows the same list. An address can be typed by
 * anyone, so every value is checked on the way in, and one a list does not know is dropped
 * rather than sent: the server finds nothing for an unknown value, which would leave an
 * empty list under a control that says "any".
 */

/** The value, when it is one of those a select offers. */
export function pickOne<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}

const RECORD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A record's id, when the value is shaped like one: anything else would be refused by the
 * API. Whether the list holds it is the page's to check, against the options it loads.
 */
export function pickId(value: string | null): string | undefined {
  return value !== null && RECORD_ID.test(value) ? value.toLowerCase() : undefined
}

/**
 * A calendar day as a date box holds it (2026-09-29), when it is a day that exists — in
 * JavaScript's calendar and in the API's, which starts at year 1: 0000-01-01 is a valid
 * JavaScript date, and a 400 from the API (Copilot on #36).
 */
export function pickDay(value: string | null): string | undefined {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) {
    return undefined
  }

  const day = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(day.getTime()) && day.toISOString().startsWith(value) ? value : undefined
}

/**
 * A whole two-letter state code, in capitals. Whether it is a real state is the server's to
 * say: the box shows what narrows the list, and "ZZ" finding nothing says so on screen.
 */
export function pickState(value: string | null): string | undefined {
  const state = value?.trim().toUpperCase()
  return state !== undefined && /^[A-Z]{2}$/.test(state) ? state : undefined
}

/** An address holding only the values that narrow or reorder, in a fixed order. */
export function searchOf(entries: ReadonlyArray<readonly [string, string | undefined]>): URLSearchParams {
  const search = new URLSearchParams()

  for (const [key, value] of entries) {
    if (value !== undefined && value !== '') {
      search.set(key, value)
    }
  }

  return search
}
